import axios from "axios";
import { ElMessage, ElLoading } from "element-plus";
import router from "@/router";

const baseURL = import.meta.env.DEV
  ? import.meta.env.VITE_API_BASE_URL_DEV // 开发接口
  : import.meta.env.VITE_API_BASE_URL_PROD; // 生产接口

// 全局 Loading 实例
let loadingInstance = null;
// 当前正在进行的请求数量
let requestCount = 0;

// Loading 白名单：只有这些接口会显示 loading
const loadingWhiteList = [
  "/api/user/login", // 登录
  // '/api/user/info',         // 获取用户信息
  // '/api/word/list',         // 单词列表
  // '/api/exam/submit',       // 提交考试
  // ... 在这里添加需要显示 loading 的接口
];

// 检查接口是否在白名单中
const isInWhiteList = (url) => {
  return loadingWhiteList.some((item) => url.includes(item));
};

// 显示 Loading
const showLoading = () => {
  if (requestCount === 0) {
    loadingInstance = ElLoading.service({
      lock: true,
      text: "",
      background: "rgba(255, 255, 255, 0.8)",
      customClass: "app-loading",
    });
  }
  requestCount++;
};

// 隐藏 Loading
const hideLoading = () => {
  requestCount--;
  if (requestCount <= 0) {
    requestCount = 0;
    if (loadingInstance) {
      loadingInstance.close();
      loadingInstance = null;
    }
  }
};

// 创建 Axios 实例
const service = axios.create({
  baseURL, // 从环境变量获取基础地址
  timeout: 5000, // 超时时间（毫秒）
  headers: {
    "Content-Type": "application/json;charset=utf-8", // 默认请求头
  },
});

// 请求拦截器
service.interceptors.request.use(
  (config) => {
    // 0. 显示全局 Loading（白名单机制）
    // 检查是否在白名单中，或者手动启用了 loading
    const shouldShowLoading = config.showLoading || isInWhiteList(config.url);

    if (shouldShowLoading && !config.hideLoading) {
      showLoading();
      config._showedLoading = true; // 标记此请求显示了 loading
    }

    // 1. 添加 Token
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers["X-Access-Token"] = token; // 按后端要求的格式添加
    }

    // 2. 可添加其他请求参数（如设备信息、语言等）
    // config.headers['X-Device'] = 'web';

    return config;
  },
  (error) => {
    // 请求错误处理
    hideLoading(); // 关闭 Loading
    console.error("请求拦截错误:", error);
    return Promise.reject(error);
  }
);

// 响应拦截器
service.interceptors.response.use(
  (response) => {
    // 关闭 Loading（只有显示了 loading 的请求才关闭）
    if (response.config._showedLoading) {
      hideLoading();
    }

    const res = response.data; // 后端返回的响应数据

    // 1. 按后端约定的状态码处理
    // 检查 success 字段或 code 是否为 200
    if (res.success === false || (res.code && res.code !== 200)) {
      // 错误提示（优先使用 message，兼容 msg）
      const errorMsg = res.message || res.msg || "操作失败";
      ElMessage({
        message: errorMsg,
        type: 'error',
        plain: true,
        offset: 100,
        customClass: 'custom-error-message',
        max: 3 // 限制最多显示3条消息
      });

      // 2. 特殊状态码处理（如 Token 过期/未授权）
      if (res.code === 401 || res.code === 403) {
        // 清除 Token 并跳转登录页
        localStorage.removeItem("token");
        router.push("/login");
      }

      return Promise.reject(res); // 将错误抛给业务组件处理
    }

    // 成功时直接返回数据（忽略外层包装）
    return res.result !== undefined ? res.result : res.data;
  },
  (error) => {
    // 关闭 Loading（只有显示了 loading 的请求才关闭）
    if (error.config?._showedLoading) {
      hideLoading();
    }

    console.error("响应拦截错误:", error);

    // 如果响应中包含后端返回的错误信息
    if (error.response?.data) {
      const errorData = error.response.data;
      
      // 优先使用后端返回的错误信息
      const errorMsg = errorData.message || errorData.msg || "服务器异常";
      ElMessage({
        message: errorMsg,
        type: 'error',
        plain: true,
        offset: 70,
        customClass: 'custom-error-message',
        max: 3 // 限制最多显示3条消息
      });

      // 特殊状态码处理（如 Token 过期/未授权）
      if (error.response.status === 401 || error.response.status === 403) {
        localStorage.removeItem("token");
        router.push("/login");
      }
    } else if (error.message) {
      // 网络错误/超时等处理
      let errorMsg = "请求失败";
      if (error.message.includes("timeout")) {
        errorMsg = "请求超时，请稍后重试";
      } else if (error.message.includes("Network Error")) {
        errorMsg = "网络错误，请检查网络连接";
      } else {
        errorMsg = error.message;
      }
      ElMessage({
        message: errorMsg,
        type: 'error',
        plain: true,
        offset: 70,
        customClass: 'custom-error-message',
        max: 3 // 限制最多显示3条消息
      });
    } else {
      ElMessage({
        message: "网络错误，请检查网络连接",
        type: 'error',
        plain: true,
        offset: 70,
        customClass: 'custom-error-message'
      });
    }

    return Promise.reject(error);
  }
);

// 封装常用请求方法
export const request = {
  get(url, params = {}) {
    return service.get("/api" + url, { params });
  },

  post(url, data = {}) {
    return service.post("/api" + url, data);
  },

  put(url, data = {}) {
    return service.put("/api" + url, data);
  },

  delete(url, params = {}) {
    return service.delete("/api" + url, { params });
  },

  // 上传文件（FormData 格式）
  upload(url, fileData) {
    return service.post("/api" + url, fileData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

export default service;
